/**
 * Plan of Record — Change Log / Decisions Register.
 *
 * The immutable audit trail. Every governance event ever recorded for this PoR,
 * newest first. Rows are appended, never edited or deleted (enforced at the RLS
 * boundary — por_governance_events has no UPDATE/DELETE policy). This is the
 * artifact that distinguishes a Plan of Record from a roadmap document.
 */

import { History } from 'lucide-react';
import type { AssembledPor, PorGovernanceEvent } from '../../types/plan-of-record';
import { PorCard, PorCardHeader, PorEmptyState, formatDate } from '../../components/plan-of-record/porUi';
import { PorNarrativeMarkdown } from '../../components/plan-of-record/PorNarrativeMarkdown';

export const EVENT_LABELS: Record<string, string> = {
  por_created: 'Plan of Record created',
  condition_change: 'Signal condition changed',
  ask_decision: 'Executive ask decided',
  status_change: 'Status changed',
};

export const EVENT_ACCENT: Record<string, string> = {
  por_created: 'bg-[var(--rule-bright)]',
  condition_change: 'bg-electric-mustard-500',
  ask_decision: 'bg-consulting-slate-blue-500',
  // Forest: the old sage repaints to the same slate as ask_decision above.
  status_change: 'bg-[var(--green)]',
};

export default function PorChangeLog({ assembled }: { assembled: AssembledPor }) {
  const events = assembled.governance_events;

  return (
    <div className="space-y-4 max-w-3xl">
      <header>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--text-ter)]">Audit</div>
        <h1 className="text-2xl font-semibold text-[var(--text-pri)] mt-0.5">Change Log</h1>
        <p className="text-sm text-[var(--text-ter)] mt-1">
          Every decision, who made it, when, and why. Appended, never edited — this is the audit trail.
        </p>
      </header>

      <PorCard>
        <PorCardHeader title="Decisions Register" count={events.length} />
        {events.length === 0 ? (
          <PorEmptyState
            icon={<History className="h-6 w-6" />}
            title="No governance events yet"
            prompt="Condition changes, ask decisions, and status changes will appear here as they happen."
          />
        ) : (
          <ol className="relative border-l border-[var(--rule)] ml-1.5 space-y-5">
            {events.map((ev) => (
              <ChangeLogRow key={ev.id} event={ev} />
            ))}
          </ol>
        )}
      </PorCard>
    </div>
  );
}

function ChangeLogRow({ event }: { event: PorGovernanceEvent }) {
  const label = EVENT_LABELS[event.event_type] ?? event.event_type.replace(/_/g, ' ');
  const accent = EVENT_ACCENT[event.event_type] ?? 'bg-[var(--rule-bright)]';

  return (
    <li className="ml-5">
      <span className={`absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full border-2 border-white ${accent}`} />
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-sm font-medium text-[var(--text-pri)]">{label}</span>
        <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)]">
          {event.event_type}
        </span>
      </div>
      {event.summary && (
        <div className="mt-0.5">
          <PorNarrativeMarkdown source={event.summary} />
        </div>
      )}
      <div className="mt-1">
        <span className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)]">Rationale:</span>
        <div className="mt-0.5">
          <PorNarrativeMarkdown source={event.rationale} emptyHint="—" />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-1 text-[11px] text-[var(--text-ter)]">
        <span>{formatDate(event.created_at)}</span>
        {event.actor_email && <span>· {event.actor_email}</span>}
      </div>
    </li>
  );
}
